import {
  ArrowDownToLine,
  ArrowUpToLine,
  Hash,
  MoveVertical,
  ShieldCheck,
  TrendingDown,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Etiqueta } from '@/components/ui/etiqueta';
import { cn } from '@/lib/utils';
import { formatoNumero, formatoPorcentaje, formatoPrecio } from '@/lib/utils/formato-precio';
import type { ConteosDeReglas, RoundMode, TipoDeRegla } from '../types/pricing-rules.types';
import type { EstadoLimite } from '../lib/nueva-regla';
import { esLimiteEnBs } from '../lib/reglas-en-palabras';

/**
 * Paso 1 · Qué límite (maqueta S2). Un radiogroup: UNA tarjeta elegida (23-sep:
 * «los grupos confundían como elegir dos»). La de movimiento tiene dos
 * casilleros, subida y bajada (decisión 8). Lote B2 (maqueta #b2-limites): siete
 * tipos; los tres nuevos llevan «Nueva» y los de Bs son sólo para un producto (D6).
 */

const pct = (a: number, b: number) => (b > 0 ? formatoPorcentaje((a / b) * 100) : '—');

const TARJETAS: Array<{ tipo: TipoDeRegla; titulo: string; ejemplo: string; nueva?: true }> = [
  {
    tipo: 'MARGEN_MINIMO',
    titulo: 'No ganar menos de…',
    ejemplo: 'Un margen mínimo sobre el precio de venta. En %.',
  },
  {
    tipo: 'MOVIMIENTO',
    titulo: 'No mover más de… por vez',
    ejemplo: 'El precio se acerca de a poco. En %.',
  },
  {
    tipo: 'SOBRE_EL_MAS_BARATO',
    titulo: 'No quedar más de… arriba del más barato',
    ejemplo: 'Para no quedar muy lejos del mercado. En %.',
  },
  {
    tipo: 'BAJO_EL_MAS_BARATO',
    titulo: 'No quedar más de… debajo del más barato',
    ejemplo: 'Para no regalar margen cuando ya sos el más barato. En %.',
    nueva: true,
  },
  {
    tipo: 'PISO_FIJO',
    titulo: 'No vender por debajo de Bs…',
    ejemplo: 'Un piso en bolivianos. Sólo para un producto.',
    nueva: true,
  },
  {
    tipo: 'TECHO_FIJO',
    titulo: 'No vender por encima de Bs…',
    ejemplo: 'Un techo en bolivianos. Sólo para un producto.',
    nueva: true,
  },
  {
    tipo: 'REDONDEO',
    titulo: 'Terminar en…',
    ejemplo: 'El redondeo comercial. Va al final.',
  },
];

/** El dibujo de cada límite (rediseño de Compite, 15-oct): se reconoce sin leer. */
const ICONOS: Record<TipoDeRegla, LucideIcon> = {
  MARGEN_MINIMO: ShieldCheck,
  MOVIMIENTO: MoveVertical,
  SOBRE_EL_MAS_BARATO: TrendingUp,
  BAJO_EL_MAS_BARATO: TrendingDown,
  PISO_FIJO: ArrowDownToLine,
  TECHO_FIJO: ArrowUpToLine,
  REDONDEO: Hash,
};

/** Los que se escriben en % en un solo casillero. */
const EN_PORCENTAJE: TipoDeRegla[] = ['MARGEN_MINIMO', 'SOBRE_EL_MAS_BARATO', 'BAJO_EL_MAS_BARATO'];

const MODOS: Array<[RoundMode, string]> = [
  ['NEAREST_99', ',99'],
  ['NEAREST_90', ',90'],
  ['NEAREST_00', 'entero'],
  ['NEAREST_50', 'múltiplo de 50'],
  ['CUSTOM', 'múltiplo a medida'],
];

function ejemploMargen(v: string): string | null {
  const m = Number(v.replace(',', '.'));
  if (!Number.isFinite(m) || m <= 0 || m >= 100) return null;
  const piso = 1000 / (1 - m / 100);
  // El precio con dos decimales, como en todo el módulo (pulido, spec 232).
  return `Con ${formatoNumero(m)} %: si cuesta 1.000, nunca baja de ${formatoPrecio(piso)}.`;
}

export function PasoLimite({
  estado,
  onChange,
  conteos,
}: {
  estado: EstadoLimite;
  onChange: (e: EstadoLimite) => void;
  conteos: ConteosDeReglas | null;
}) {
  const cat = conteos?.catalogo;
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Elegí <strong>uno</strong>. Los de % sirven para muchos productos: a cada uno le dan su
        propio número. Los de Bs son para un solo producto. En el paso siguiente decís a quién
        alcanza.
      </p>
      <div
        role="radiogroup"
        aria-label="Qué límite"
        className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3"
      >
        {TARJETAS.map((t) => {
          const elegida = estado.tipo === t.tipo;
          const Icono = ICONOS[t.tipo];
          return (
            <div
              key={t.tipo}
              data-elegida={elegida || undefined}
              className={cn(
                'rounded-xl border bg-card p-4 transition-colors hover:border-primary/50',
                elegida && 'border-primary bg-primary/5 ring-1 ring-primary hover:border-primary'
              )}
            >
              <label className="flex cursor-pointer items-start gap-3">
                <span
                  aria-hidden="true"
                  className={cn(
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
                    elegida ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary'
                  )}
                >
                  <Icono className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2 font-semibold text-foreground">
                    {t.titulo}
                    {t.nueva && <Etiqueta tono="info">Nueva</Etiqueta>}
                  </span>
                  <span className="mt-0.5 block text-sm text-muted-foreground">{t.ejemplo}</span>
                </span>
                <input
                  type="radio"
                  name="tipo-de-limite"
                  aria-label={t.titulo}
                  checked={elegida}
                  // Un % no es un monto en Bs (lote B2): al cambiar de uno a otro, vacío.
                  onChange={() =>
                    onChange({
                      ...estado,
                      tipo: t.tipo,
                      valor: esLimiteEnBs(t.tipo) === esLimiteEnBs(estado.tipo) ? estado.valor : '',
                    })
                  }
                  className="mt-1 h-4 w-4 shrink-0 accent-primary"
                />
              </label>

              {elegida && EN_PORCENTAJE.includes(t.tipo) && (
                <div className="mt-3 space-y-1">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="valor-limite">Porcentaje</Label>
                    <span className="inline-flex items-center gap-1 whitespace-nowrap">
                      <Input
                        id="valor-limite"
                        inputMode="decimal"
                        className="w-24"
                        value={estado.valor}
                        onChange={(e) => onChange({ ...estado, valor: e.target.value })}
                      />
                      %
                    </span>
                  </div>
                  {t.tipo === 'MARGEN_MINIMO' && (
                    <>
                      {ejemploMargen(estado.valor) && (
                        <p className="text-xs text-muted-foreground">
                          {ejemploMargen(estado.valor)}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Necesita el costo · lo tiene el {cat ? pct(cat.conCosto, cat.total) : '—'}{' '}
                        del catálogo
                      </p>
                    </>
                  )}
                  {t.tipo === 'SOBRE_EL_MAS_BARATO' && (
                    <p className="text-xs text-muted-foreground">
                      Con 10 %: si el más barato está a 900, nunca sube de 990. Necesita competencia
                      · la tiene el {cat ? pct(cat.conCompetencia, cat.total) : '—'} del catálogo
                    </p>
                  )}
                  {t.tipo === 'BAJO_EL_MAS_BARATO' && (
                    <p className="text-xs text-muted-foreground">
                      Con 10 %: si el más barato está a 900, nunca baja de 810. Necesita competencia
                      · la tiene el {cat ? pct(cat.conCompetencia, cat.total) : '—'} del catálogo
                    </p>
                  )}
                </div>
              )}

              {elegida && (t.tipo === 'PISO_FIJO' || t.tipo === 'TECHO_FIJO') && (
                <div className="mt-3 space-y-1">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="monto-limite">Monto</Label>
                    <span className="inline-flex items-center gap-1 whitespace-nowrap">
                      Bs
                      <Input
                        id="monto-limite"
                        inputMode="decimal"
                        className="w-32"
                        value={estado.valor}
                        onChange={(e) => onChange({ ...estado, valor: e.target.value })}
                      />
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Un monto fijo no sirve para una categoría con precios distintos: en el paso
                    siguiente elegís el producto.
                  </p>
                </div>
              )}

              {elegida && t.tipo === 'MOVIMIENTO' && (
                <div className="mt-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="subida-maxima" className="w-32">
                      Subida máxima
                    </Label>
                    <Input
                      id="subida-maxima"
                      inputMode="decimal"
                      className="w-24"
                      value={estado.subida}
                      onChange={(e) => onChange({ ...estado, subida: e.target.value })}
                    />
                    <span>%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="bajada-maxima" className="w-32">
                      Bajada máxima
                    </Label>
                    <Input
                      id="bajada-maxima"
                      inputMode="decimal"
                      className="w-24"
                      value={estado.bajada}
                      onChange={(e) => onChange({ ...estado, bajada: e.target.value })}
                    />
                    <span>%</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Contra el precio de hoy. Dejá vacío el sentido que no querés limitar; 0 % es
                    «nunca». Con 15 % de bajada: vale 1.000 y la estrategia pide 700; esta vez baja
                    a 850 y en la próxima corrida, otro 15 %.
                  </p>
                </div>
              )}

              {elegida && t.tipo === 'REDONDEO' && (
                <div className="mt-3 space-y-2">
                  <div
                    className="flex flex-wrap gap-2 text-xs"
                    role="group"
                    aria-label="Cómo termina"
                  >
                    {MODOS.map(([m, txt]) => (
                      <button
                        key={m}
                        type="button"
                        aria-pressed={estado.mode === m}
                        onClick={() => onChange({ ...estado, mode: m })}
                        className={cn(
                          'rounded-full border px-2 py-0.5',
                          estado.mode === m && 'border-primary bg-primary/10'
                        )}
                      >
                        {txt}
                      </button>
                    ))}
                  </div>
                  {estado.mode === 'CUSTOM' && (
                    <div className="flex items-center gap-2">
                      <Label htmlFor="multiplo">Múltiplo de</Label>
                      <Input
                        id="multiplo"
                        inputMode="decimal"
                        className="w-24"
                        value={estado.customValue}
                        onChange={(e) => onChange({ ...estado, customValue: e.target.value })}
                      />
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Si redondear dejara el precio por debajo del margen mínimo, redondea para
                    arriba.
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="rounded-md bg-muted/40 p-3 text-xs text-muted-foreground">
        <strong>¿Querés mover precios?</strong> Eso lo hace una estrategia (igualar al más barato,
        bajar un %, apuntar a un margen). Una regla nunca decide un precio: sólo pone el límite.
      </p>
    </div>
  );
}
